/**
 * Componente SalesHistory
 * 
 * Este componente se encarga de mostrar el historial de ventas en una tabla.
 * Permite filtrar las ventas por un rango de fechas y paginar los resultados.
 * 
 * Funcionalidades:
 * - Filtrado por rango de fechas.
 * - Paginación de resultados.
 * - Visualización de detalles de cada venta.
 * - Descarga de comprobantes en formato PDF.
 * 
 * Hooks:
 * - useState: Para manejar el estado local del componente.
 * - useEffect: Para realizar la carga inicial de datos.
 * 
 * Dependencias:
 * - apiRepository: Para realizar las llamadas a la API.
 * - DataTableWrapper: Componente para mostrar los datos en una tabla.
 * - CalendarRangeField: Componente para seleccionar el rango de fechas.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRepository } from '../../../api/apiRepository';
import { Payments } from '../../../core/Interface/repositoryInterfaces';
import { getDefaultWeekRange } from '../../../core/utils/dateUtils';
import { formatToCustomDate } from '../../../core/utils/utils';
import { useResponsiveColumns } from '../../../core/hooks/useResponsiveColumnsCoupons';
import DataTableWrapper from '../common/dataTable/dataTableWrapper';
import * as Icon from 'react-feather';
import { RangeCalendar } from '../../components/inputs';
import { all_routes } from '../../../core/data/routes/all_routes';

const SalesHistory = () => {
  const [data, setData] = useState<Payments[]>([]);
  const [dateRange, setDateRange] = useState(getDefaultWeekRange());
  const [totalRecords, setTotalRecords] = useState<number>(-1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const routes = all_routes;
  const navigate = useNavigate();

  const fetchSales = async (pageNumber = currentPage, pageSize = rows) => {
    try {
      const [startDate, endDate] = dateRange;

      if (!startDate || !endDate) {
        return;
      }

      const response = await apiRepository.getPayments(
        formatToCustomDate(startDate),
        formatToCustomDate(endDate),
        pageNumber || 0,
        pageSize || 10
      );
      const totalRecords = response.data.data.totalElements;
      setData(response.data.data.content);
      setTotalRecords(totalRecords);
    } catch (error) {
      console.error("Error al obtener el historial de compras:", error);
    }
  };

  useEffect(() => {
    fetchSales();
  }, [dateRange]);

  const handleDateChange = (e) => {
    setDateRange(e.value);
  };
  const handleCustomActionClick = (rowData: any) => {
    if (rowData.pathPdf) {
      window.open(rowData.pathPdf, '_blank');
    }
  };

  const columns = [
    { field: "userFullName", header: "Cliente" },
    { field: "amount", header: "Monto Total", body: (rowData: any) => `S/. ${Number(rowData.amount).toFixed(2)}` },
    { field: "createdAt", header: "Fecha", body: (rowData: any) => rowData.createdAt.split('T')[0] },
    {
      field: "status",
      header: "Estado",
      body: (rowData: any) => (
        <span className={rowData.status === 'COMPLETED' ? 'badge-completed' : 'badge-pending'}>
          {rowData.status === 'COMPLETED' ? 'Completado' : 'Pendiente'}
        </span>
      ),
    },
    {
      field: "paymentDetails",
      header: "Paquetes",
      body: (rowData: any) => (
        <ul className="package-list m-0 p-0">
          {rowData.paymentDetails.map((detail: any, index: number) => (
            <li key={index}>
              {detail.packetName} ({detail.quantity}) - S/. {Number(detail.amount).toFixed(2)}
            </li>
          ))}
        </ul>
      ),
    },
  ];

  const getColumns = useResponsiveColumns(columns);

  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
    const pageNumber = event.first / event.rows;
    setCurrentPage(pageNumber);
    fetchSales(pageNumber, event.rows);
  };

  return (
    <div className="page-wrapper page-settings">
      <div className="content">
        <div className="content-page-header content-page-headersplit">
          <h5>Historial de Compras</h5>
          <div className="list-btn d-flex align-items-center">
          <button
              className="btn btn-primary-admin me-3 mt-3"
              onClick={() => navigate(routes.addPayment + "?retry=true")}
            >
              <i className="fa fa-plus me-2" /> Agregar Pago
            </button>
            <div className="col mb-0">
              <RangeCalendar
                label="Rango de fechas"
                value={dateRange}
                onChange={handleDateChange}
              />
            </div>

          </div>
        </div>
        <div className="row">
          <div className="col-12 ">
            <DataTableWrapper
              data={data || []}
              columns={getColumns()}
              onCustomAction={handleCustomActionClick}
              customIcon={Icon.FileText}
              paginator
              showGridlines
              rowsPerPage={rows}
              totalRecords={totalRecords}
              first={first}
              onPageChange={onPageChange}
              emptyMessage="No se encontraron compras."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesHistory;